import { CREATE_SPRITE_WORKER_URL } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { getBlockedFilteredImagePromptMessage, getBlockedUnrecognizedPromptMessage } from '../danbooruTags.js';
import { getNsfwEnabled, getStylePreference, setStylePreference, normalizeStyle } from '../../utils/userPrefs.js';
import { showError, showInfo, showSuccess } from '../../utils/UnifiedNotificationSystem.js';
import {
  getAuthToken,
  getUserCredits,
  showFreeAccountModalIfNeeded,
  syncSpriteCredits,
} from '../videoScheduleApi.js';
import {
  buildLocalCreditResult,
  computeStandaloneImageCost,
  interpolateCreditTemplate,
} from '../creditCostApi.js';
import { refreshRichPromptEditor } from '../richPromptEditor.js';
import { initializeNegativePromptAutocomplete } from '../promptAutocomplete.js';
import {
  extractBraceTokenNames,
  initStandalonePromptHelpers,
  isLinkedBraceToken,
  lookupPromptRefId,
  normalizePromptTokenName,
  promptRefsMatchingPrompt,
  rememberPromptRef,
} from '../utils/standalonePromptHelpers.js';
import { setPreviewCardState } from './previewCardState.js';
import { IMAGE_KINDS } from './parseCreateVideoConfig.js';
import { waitForQueuedImage, parseOutputKeyFromPendingUrl } from '../waitForQueuedImage.js';
import { notifyVideoContainerStarting } from '../waitForQueuedVideo.js';
import { adoptWaitEstimate, remainingWaitSeconds } from '../waitEstimate.js';
import {
  fetchStandaloneImageObjectUrl,
  hdUpgradeCostLabel,
  requestStandaloneHdUpgrade,
} from '../standaloneHdUpgrade.js';
import { consumeSimilarStandaloneImage } from '../similarStandaloneImage.js';
import { bindGenerationSeedControl, parseGenerationSeed } from '../generationSeed.js';
import { fetchStandaloneImageRefs } from '../standaloneMediaApi.js';
import {
  STANDALONE_IMAGE_SIZE_PRESETS,
  clampStandaloneImageSize,
  matchStandaloneImagePreset,
  formatStandaloneImageRatio,
  applyLockedStandaloneImageSize,
} from './standaloneImageSize.js';

const MAX_LORAS = 3;
const SUBMIT_CLICK_COOLDOWN_MS = 1000;

let imageSeedControl = null;
let submitClickAllowedAt = 0;

let resolveCharacters = () => [];

const promptRefs = {
  characters: {},
  outfits: {},
  locations: {},
};

const rememberFreePromptRef = (kind, name, id) => rememberPromptRef(promptRefs, kind, name, id);

const isFreeLinkedBraceToken = (name) => isLinkedBraceToken(promptRefs, name);

const applyPromptRefs = (raw) => {
  ['characters', 'outfits', 'locations'].forEach((kind) => {
    const map = raw?.[kind];
    if (!map || typeof map !== 'object' || Array.isArray(map)) return;
    Object.entries(map).forEach(([name, id]) => rememberFreePromptRef(kind, name, id));
  });
};

const applySimilarPromptRefs = async (els, jobId) => {
  const token = await getAuthToken();
  if (!token) return;
  try {
    const result = await fetchStandaloneImageRefs({ token, jobId });
    applyPromptRefs(result?.promptRefs);
    if (els.removeBackground && result?.promptRefs?.remove_background === true) {
      els.removeBackground.checked = true;
    }
    refreshRichPromptEditor(els.prompt);
    if (els.prompt) {
      els.prompt.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      syncCharacterLoraRows(els);
    }
  } catch (error) {
    console.warn('load similar image prompt refs failed', error);
  }
};

const getUnlinkedBraceTokens = (prompt) => (
  extractBraceTokenNames(prompt).filter((name) => !isFreeLinkedBraceToken(name))
);

const bareOutfitWithoutCatalogPrompt = (prompt) => (
  extractBraceTokenNames(prompt).find((token) => {
    if (token.includes(':')) return false;
    const normalized = normalizePromptTokenName(token);
    if (normalized === 'default' || normalized === 'default outfit') return true;
    const outfitId = lookupPromptRefId(promptRefs.outfits, token);
    return outfitId != null && Number(outfitId) === 0;
  }) || ''
);

const getCreateVideoT = (key, fallback) => {
  const lang = window.getCurrentLanguage ? window.getCurrentLanguage() : 'en';
  const t = window.translations?.[lang]?.['create-video'] ?? window.translations?.en?.['create-video'];
  return t?.[key] ?? fallback;
};

const queryFreeImageElements = () => ({
  kindTabs: document.querySelectorAll('[data-tablist="image-kind"] .create-mode-tab'),
  spritePanel: document.getElementById('panel-image-sprite'),
  freePanel: document.getElementById('panel-image-free'),
  prompt: document.getElementById('free-image-prompt'),
  negative: document.getElementById('free-image-negative'),
  styleAnime: document.getElementById('free-image-style-anime'),
  styleRealism: document.getElementById('free-image-style-realism'),
  sizePresets: document.querySelectorAll('[data-size-preset]'),
  width: document.getElementById('free-image-width'),
  height: document.getElementById('free-image-height'),
  ratio: document.getElementById('free-image-ratio'),
  ratioLock: document.getElementById('free-image-ratio-lock'),
  sizeError: document.getElementById('free-image-size-error'),
  removeBackground: document.getElementById('free-image-remove-background'),
  loraRows: document.getElementById('free-image-lora-rows'),
  addLora: document.getElementById('free-image-add-lora'),
  seed: document.getElementById('free-image-seed'),
  seedLock: document.getElementById('free-image-seed-lock'),
  seedReroll: document.getElementById('free-image-seed-reroll'),
  creditInfo: document.getElementById('credit-info-free-image'),
  submit: document.getElementById('validate-free-image'),
  submitWrap: document.getElementById('validate-free-image-wrap'),
  submitTooltip: document.getElementById('validate-free-image-tooltip'),
  previewSection: document.getElementById('free-image-preview-section'),
  previewCard: document.getElementById('free-image-preview-card'),
  eta: document.getElementById('free-image-eta'),
  etaCountdown: document.getElementById('free-image-eta-countdown'),
  etaLabel: document.getElementById('free-image-eta-label'),
  hdActions: document.getElementById('free-image-hd-actions'),
  upgradeHd: document.getElementById('free-image-upgrade-hd'),
  upgradeCredits: document.getElementById('free-image-upgrade-credits'),
  upgradedHd: document.getElementById('free-image-upgraded-hd'),
});

const setButtonBusy = (btn, isBusy) => {
  if (!btn) return;
  btn.classList.toggle('is-busy', isBusy);
  btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
};

const characterHasStoredLora = (name) => {
  const id = lookupPromptRefId(promptRefs.characters, name);
  const chars = resolveCharacters();
  const byId = chars.find((entry) => String(entry.id) === String(id));
  if (byId) return Boolean(byId.hasLora);
  const byName = chars.find((entry) => String(entry.name) === String(name));
  return Boolean(byName?.hasLora);
};

const parseLoraRow = (rowEl) => {
  const id = rowEl.querySelector('[data-free-lora-id]')?.value?.trim() || '';
  if (!id) return null;
  const strengthRaw = rowEl.querySelector('[data-free-lora-strength]')?.value?.trim() || '';
  const strengthParsed = strengthRaw ? parseFloat(strengthRaw) : NaN;
  const isCharacter = rowEl.hasAttribute('data-character-lora');
  return {
    id,
    source: isCharacter ? 'character' : 'civitai',
    strength: Number.isFinite(strengthParsed) ? strengthParsed : 1.0,
  };
};

const collectLoras = (els) => {
  if (!getNsfwEnabled()) return [];
  const rows = Array.from(els.loraRows?.querySelectorAll('[data-free-lora-row]') || []);
  return rows.map(parseLoraRow).filter(Boolean);
};

const getSelectedStyle = (els) => (
  els.styleRealism?.classList.contains('create-mode-tab--active') ? 'realism' : 'anime'
);

let aspectLockOn = false;
let lockedRatio = 896 / 1152;
let applyingSize = false;

const sizeFromInputs = (els) => clampStandaloneImageSize(els.width?.value, els.height?.value);

const computeFreeImageCost = (els, size = sizeFromInputs(els)) => computeStandaloneImageCost({
  style: getSelectedStyle(els),
  loraCount: collectLoras(els).length,
  width: size.width,
  height: size.height,
});

const updateCredits = (els) => {
  if (!els.creditInfo) return;
  const size = sizeFromInputs(els);
  const total = computeFreeImageCost(els, size);
  const result = buildLocalCreditResult(total);
  const line = els.creditInfo.querySelector('[data-credit-line]');
  const template = getCreateVideoT('credit_per_image', '{count} image = {cost} credits (You have {available} credits)');
  if (line) {
    line.textContent = interpolateCreditTemplate(template, {
      count: 1,
      cost: result.total,
      available: result.available_credits,
    });
  }
  els.creditInfo.classList.toggle('insufficient', !result.has_enough_credits);
  validateForm(els);
};

const hideLoraLimitTooltip = (els) => {
  const tooltip = els.submitTooltip;
  if (!tooltip) return;
  tooltip.hidden = true;
  tooltip.classList.remove('is-visible');
};

const showLoraLimitTooltip = (els) => {
  const tooltip = els.submitTooltip;
  if (!tooltip || els.submitWrap?.getAttribute('data-lora-limit') !== 'true') return;
  tooltip.textContent = getCreateVideoT('free_image_lora_limit', 'Remove a LoRA before creating an image (max 3).');
  tooltip.hidden = false;
  tooltip.classList.add('is-visible');
};

const syncLoraLimitTooltip = (els, overLora) => {
  const wrap = els.submitWrap;
  const tooltip = els.submitTooltip;
  if (!wrap) return;
  if (overLora) {
    wrap.setAttribute('data-lora-limit', 'true');
    wrap.setAttribute('aria-describedby', 'validate-free-image-tooltip');
    if (tooltip) {
      tooltip.textContent = getCreateVideoT('free_image_lora_limit', 'Remove a LoRA before creating an image (max 3).');
    }
    return;
  }
  wrap.removeAttribute('data-lora-limit');
  wrap.removeAttribute('aria-describedby');
  hideLoraLimitTooltip(els);
};

const bindLoraLimitTooltip = (els) => {
  const wrap = els.submitWrap;
  if (!wrap || wrap.dataset.loraTooltipBound === 'true') return;
  wrap.dataset.loraTooltipBound = 'true';

  wrap.addEventListener('pointerenter', () => {
    if (wrap.getAttribute('data-lora-limit') !== 'true') return;
    showLoraLimitTooltip(els);
  });
  wrap.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'touch') return;
    hideLoraLimitTooltip(els);
  });
  wrap.addEventListener('pointerup', (event) => {
    if (wrap.getAttribute('data-lora-limit') !== 'true') return;
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    event.preventDefault();
    const tooltip = els.submitTooltip;
    if (tooltip?.classList.contains('is-visible')) hideLoraLimitTooltip(els);
    else showLoraLimitTooltip(els);
  });
  document.addEventListener('pointerdown', (event) => {
    if (wrap.getAttribute('data-lora-limit') !== 'true') return;
    if (wrap.contains(event.target)) return;
    hideLoraLimitTooltip(els);
  });
};

const validateForm = (els) => {
  if (!els.submit) return;
  const prompt = els.prompt?.value?.trim() || '';
  const loraCount = collectLoras(els).length;
  const overLora = loraCount > MAX_LORAS;
  const size = sizeFromInputs(els);
  const cost = computeFreeImageCost(els, size);
  els.submit.disabled = !(prompt && !overLora && size.ok && cost <= getUserCredits());
  syncLoraLimitTooltip(els, overLora);
};

const syncAddLoraButton = (els) => {
  const count = els.loraRows?.querySelectorAll('[data-free-lora-row]')?.length || 0;
  const atMax = count >= MAX_LORAS;
  if (!els.addLora) return;
  els.addLora.disabled = atMax;
  els.addLora.hidden = atMax;
};

const handleRemoveLoraRow = (els, rowEl) => {
  if (rowEl?.hasAttribute('data-character-lora')) return;
  rowEl.remove();
  if ((els.loraRows?.querySelectorAll('[data-free-lora-row]')?.length || 0) === 0) {
    addLoraRow(els);
  }
  syncAddLoraButton(els);
  updateCredits(els);
};

const isEmptyUserLoraRow = (rowEl) => {
  if (!rowEl || rowEl.hasAttribute('data-character-lora')) return false;
  const id = rowEl.querySelector('[data-free-lora-id]')?.value?.trim() || '';
  const strength = rowEl.querySelector('[data-free-lora-strength]')?.value?.trim() || '';
  return !id && !strength;
};

const openFreeImageAdvanced = () => {
  const details = document.getElementById('free-image-advanced-lora');
  if (details) details.open = true;
};

const addCharacterLoraRow = (els, name) => {
  if (!els.loraRows || !name) return;
  const row = document.createElement('div');
  row.className = 'step2-lora-row';
  row.setAttribute('data-free-lora-row', '');
  row.setAttribute('data-character-lora', name);

  const idInput = document.createElement('input');
  idInput.type = 'text';
  idInput.className = 'form-input kv-key';
  idInput.setAttribute('data-free-lora-id', '');
  idInput.value = `{${name}}`;
  idInput.readOnly = true;
  idInput.setAttribute('aria-label', `Character LoRA ${name}`);
  idInput.tabIndex = -1;

  const strengthInput = document.createElement('input');
  strengthInput.type = 'text';
  strengthInput.className = 'form-input kv-value';
  strengthInput.setAttribute('data-free-lora-strength', '');
  strengthInput.value = '1';
  strengthInput.readOnly = true;
  strengthInput.setAttribute('aria-label', 'Character LoRA strength');
  strengthInput.tabIndex = -1;

  row.append(idInput, strengthInput);
  els.loraRows.appendChild(row);
  openFreeImageAdvanced();
  syncAddLoraButton(els);
};

const ensureCharacterLoraRow = (els, name) => {
  const token = String(name || '').trim();
  if (!els.loraRows || !token) return;
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(token) : token.replace(/"/g, '\\"');
  const existing = els.loraRows.querySelector(`[data-character-lora="${escaped}"]`);
  if (existing) return;
  const empty = Array.from(els.loraRows.querySelectorAll('[data-free-lora-row]')).find(isEmptyUserLoraRow);
  empty?.remove();
  addCharacterLoraRow(els, token);
};

const syncCharacterLoraRows = (els) => {
  const prompt = els.prompt?.value || '';
  const tokens = new Set(
    extractBraceTokenNames(prompt).filter((name) => !name.includes(':')),
  );
  Array.from(els.loraRows?.querySelectorAll('[data-character-lora]') || []).forEach((row) => {
    const name = row.getAttribute('data-character-lora');
    if (!tokens.has(name)) row.remove();
  });
  tokens.forEach((name) => {
    if (!characterHasStoredLora(name)) return;
    ensureCharacterLoraRow(els, name);
  });
  if ((els.loraRows?.querySelectorAll('[data-free-lora-row]')?.length || 0) === 0) {
    addLoraRow(els);
  }
  syncAddLoraButton(els);
  updateCredits(els);
};

const addLoraRow = (els) => {
  const count = els.loraRows?.querySelectorAll('[data-free-lora-row]')?.length || 0;
  if (!els.loraRows || count >= MAX_LORAS) return;

  const row = document.createElement('div');
  row.className = 'step2-lora-row';
  row.setAttribute('data-free-lora-row', '');

  const idInput = document.createElement('input');
  idInput.type = 'text';
  idInput.className = 'form-input kv-key';
  idInput.setAttribute('data-free-lora-id', '');
  idInput.setAttribute('data-translate-placeholder', 'lora_id_placeholder');
  idInput.placeholder = getCreateVideoT('lora_id_placeholder', 'civitai version ID (not model id!)');
  idInput.setAttribute('aria-label', 'LoRA civitai version ID');
  idInput.tabIndex = 0;
  idInput.maxLength = 500;

  const strengthInput = document.createElement('input');
  strengthInput.type = 'text';
  strengthInput.className = 'form-input kv-value';
  strengthInput.setAttribute('data-free-lora-strength', '');
  strengthInput.setAttribute('data-translate-placeholder', 'lora_strength_placeholder');
  strengthInput.placeholder = getCreateVideoT('lora_strength_placeholder', 'strength (e.g. 1.0)');
  strengthInput.setAttribute('aria-label', 'LoRA strength');
  strengthInput.tabIndex = 0;
  strengthInput.maxLength = 50;

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'btn';
  removeBtn.setAttribute('aria-label', getCreateVideoT('free_image_remove_lora', 'Remove LoRA'));
  removeBtn.tabIndex = 0;
  removeBtn.innerHTML = '<i class="fas fa-times" aria-hidden="true"></i>';

  const handleInput = () => updateCredits(els);
  idInput.addEventListener('input', handleInput);
  strengthInput.addEventListener('input', handleInput);
  removeBtn.addEventListener('click', () => handleRemoveLoraRow(els, row));
  removeBtn.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleRemoveLoraRow(els, row);
  });

  row.append(idInput, strengthInput, removeBtn);
  els.loraRows.appendChild(row);
  syncAddLoraButton(els);
};

const syncSizePresetButtons = (els, size) => {
  const preset = matchStandaloneImagePreset(size.width, size.height);
  els.sizePresets.forEach((btn) => {
    const isActive = Boolean(preset && btn.dataset.sizePreset === preset.id);
    btn.classList.toggle('create-mode-tab--active', isActive);
    btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
  });
};

const syncPreviewFrame = (els, size) => {
  const card = els.previewCard;
  if (!card || !size.height) return;
  card.style.setProperty('--free-image-preview-width', `${size.width}px`);
  card.style.setProperty('--free-image-preview-ratio', `${size.width} / ${size.height}`);
};

const syncSizeChrome = (els, size) => {
  syncSizePresetButtons(els, size);
  syncPreviewFrame(els, size);
  if (els.ratio) els.ratio.textContent = formatStandaloneImageRatio(size.width, size.height);
  const over = !size.ok;
  els.sizeError?.classList.toggle('hidden', !over);
  if (els.sizeError) {
    els.sizeError.hidden = !over;
    els.sizeError.textContent = getCreateVideoT(
      'free_image_size_max',
      'Maximum dimensions are 1024x1024, please reduce either width or height',
    );
  }
};

const writeSizeInputs = (els, size) => {
  applyingSize = true;
  if (els.width) els.width.value = String(size.width);
  if (els.height) els.height.value = String(size.height);
  applyingSize = false;
};

const applySizeToInputs = (els, width, height, { refreshLockRatio = false } = {}) => {
  const size = clampStandaloneImageSize(width, height);
  writeSizeInputs(els, size);
  if (refreshLockRatio && size.height) lockedRatio = size.width / size.height;
  syncSizeChrome(els, size);
  updateCredits(els);
  return size;
};

const readClampedSize = (els) => applySizeToInputs(els, els.width?.value, els.height?.value);

const setAspectLock = (els, locked) => {
  aspectLockOn = Boolean(locked);
  els.ratioLock?.setAttribute('aria-pressed', aspectLockOn ? 'true' : 'false');
  const icon = els.ratioLock?.querySelector('i');
  if (icon) {
    icon.classList.toggle('fa-lock', aspectLockOn);
    icon.classList.toggle('fa-unlock', !aspectLockOn);
  }
  if (aspectLockOn) {
    const size = sizeFromInputs(els);
    if (size.height) lockedRatio = size.width / size.height;
  }
};

const applyLockedChange = (els, changed, value) => {
  const size = applyLockedStandaloneImageSize({ changed, value, ratio: lockedRatio });
  applyingSize = true;
  if (changed === 'width') {
    if (els.height) els.height.value = String(size.height);
  } else if (els.width) {
    els.width.value = String(size.width);
  }
  applyingSize = false;
  syncSizeChrome(els, clampStandaloneImageSize(els.width?.value, els.height?.value));
  updateCredits(els);
  return size;
};

const setStyle = (els, style) => {
  const normalized = normalizeStyle(style);
  const isRealism = normalized === 'realism';
  els.styleAnime?.classList.toggle('create-mode-tab--active', !isRealism);
  els.styleRealism?.classList.toggle('create-mode-tab--active', isRealism);
  els.styleAnime?.setAttribute('aria-checked', isRealism ? 'false' : 'true');
  els.styleRealism?.setAttribute('aria-checked', isRealism ? 'true' : 'false');
  setStylePreference(normalized);
  updateCredits(els);
};

const setImageKind = (els, kind) => {
  const isFree = kind === IMAGE_KINDS.FREE;
  els.kindTabs.forEach((tab) => {
    const isActive = tab.id === `image-kind-${kind}`;
    tab.classList.toggle('create-mode-tab--active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  });
  els.spritePanel?.classList.toggle('hidden', isFree);
  if (els.spritePanel) els.spritePanel.hidden = isFree;
  els.freePanel?.classList.toggle('hidden', !isFree);
  if (els.freePanel) els.freePanel.hidden = !isFree;
};

const setPreviewVisible = (els, shouldShow) => {
  els.previewSection?.classList.toggle('hidden', !shouldShow);
  els.previewSection?.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
};

let waitAbort = null;
let waitGeneration = 0;
let etaTimer = null;
let previewJob = null;

const abortActiveWait = () => {
  waitAbort?.abort();
  waitAbort = null;
  if (etaTimer) {
    clearInterval(etaTimer);
    etaTimer = null;
  }
};

const beginWait = () => {
  abortActiveWait();
  waitAbort = new AbortController();
  waitGeneration += 1;
  return { signal: waitAbort.signal, generation: waitGeneration };
};

const setEtaVisible = (els, visible) => {
  els.eta?.classList.toggle('hidden', !visible);
  const loader = els.previewCard?.querySelector('.three-dots-loader');
  if (visible) loader?.classList.add('hidden');
};

const startEtaOverlay = (els, jobKey) => {
  if (etaTimer) {
    clearInterval(etaTimer);
    etaTimer = null;
  }
  let waitEstimate = null;
  setEtaVisible(els, true);
  if (els.etaLabel) {
    els.etaLabel.textContent = getCreateVideoT('generating_preview', 'Generating...');
  }
  if (els.etaCountdown) els.etaCountdown.textContent = '';

  const renderCountdown = () => {
    if (els.etaLabel && waitEstimate?.stalled) {
      els.etaLabel.textContent = 'Instance stalled, requeued';
    }
    const remaining = remainingWaitSeconds(waitEstimate);
    if (els.etaCountdown) {
      if (waitEstimate?.stalled) els.etaCountdown.textContent = '';
      else if (remaining != null) els.etaCountdown.textContent = remaining > 0 ? `${remaining}s` : '';
    }
  };

  etaTimer = setInterval(renderCountdown, 1000);

  return (data) => {
    waitEstimate = adoptWaitEstimate(waitEstimate, data);
    renderCountdown();
    notifyVideoContainerStarting(data, jobKey, 'image');
  };
};

const stopEtaOverlay = (els) => {
  if (etaTimer) {
    clearInterval(etaTimer);
    etaTimer = null;
  }
  setEtaVisible(els, false);
};

const setHdActionState = (els, { mode = 'hidden', style = 'anime', loraCount = 0, width, height } = {}) => {
  if (!els.hdActions) return;
  const showActions = mode === 'upgrade' || mode === 'done';
  els.hdActions.classList.toggle('hidden', !showActions);
  els.upgradeHd?.classList.toggle('hidden', mode !== 'upgrade');
  els.upgradeCredits?.classList.toggle('hidden', mode !== 'upgrade');
  els.upgradedHd?.classList.toggle('hidden', mode !== 'done');
  if (mode === 'upgrade' && els.upgradeCredits) {
    const { label } = hdUpgradeCostLabel({
      style,
      loraCount,
      width,
      height,
      template: getCreateVideoT(
        'free_image_upgrade_credits',
        'HD upgrade = {cost} credits (You have {available} credits)',
      ),
    });
    els.upgradeCredits.textContent = label;
  }
};

const waitForPreviewJob = async (els, {
  pendingUrl,
  outputKey,
  style,
  loraCount,
  width,
  height,
  keepImage = false,
  markHd = false,
} = {}) => {
  const { signal, generation } = beginWait();
  previewJob = { pendingUrl, outputKey, style, loraCount, width, height, isHd: false };
  setHdActionState(els, { mode: 'hidden' });
  if (!keepImage) {
    setPreviewCardState(els.previewCard, {
      loading: true,
      placeholderText: getCreateVideoT('generating_preview', 'Generating...'),
    });
  }
  const onEstimate = startEtaOverlay(els, outputKey || pendingUrl);
  try {
    const imageUrl = await waitForQueuedImage({
      pendingUrl,
      outputKey,
      style,
      signal,
      onEstimate,
      requireHd: markHd,
    });
    if (generation !== waitGeneration) return;
    stopEtaOverlay(els);
    const token = await getAuthToken();
    const objectUrl = await fetchStandaloneImageObjectUrl({
      token,
      outputKey,
      bust: markHd,
    });
    setPreviewCardState(els.previewCard, {
      imgUrl: objectUrl || imageUrl,
      showImg: true,
    });
    previewJob = { ...previewJob, isHd: Boolean(markHd), pendingUrl: objectUrl || imageUrl };
    setHdActionState(els, {
      mode: markHd ? 'done' : 'upgrade',
      style,
      loraCount,
      width,
      height,
    });
    showSuccess(getCreateVideoT('free_image_ready', 'Image ready'));
  } catch (error) {
    if (generation !== waitGeneration || error?.name === 'AbortError') return;
    stopEtaOverlay(els);
    if (!keepImage) {
      setPreviewCardState(els.previewCard, {
        placeholderText: error.message || getCreateVideoT('free_image_failed', 'Failed to create image'),
      });
    }
    if (error?.message === 'Image generation failed' || error?.message === 'HD upgrade failed') {
      showError(getCreateVideoT('free_image_failed', 'Failed to create image'));
    } else {
      showError(getCreateVideoT('free_image_timeout', 'Image generation is taking longer than expected. Check your gallery later.'));
    }
    if (keepImage) {
      setHdActionState(els, { mode: 'upgrade', style, loraCount, width, height });
    }
  }
};

const handleSubmitClick = (els) => {
  const now = Date.now();
  if (now < submitClickAllowedAt) return;
  submitClickAllowedAt = now + SUBMIT_CLICK_COOLDOWN_MS;
  void handleSubmit(els);
};

const handleSubmit = async (els) => {
  const prompt = els.prompt?.value?.trim() || '';
  if (!prompt) {
    showError(getCreateVideoT('free_image_prompt_required', 'Please enter a prompt'));
    return;
  }
  const unlinked = getUnlinkedBraceTokens(prompt);
  if (unlinked.length) {
    const template = getCreateVideoT(
      'free_image_unlinked_helper',
      'Select {name} from the Character, Outfit, or Background helpers so we can link it.',
    );
    showError(interpolateCreditTemplate(template, { name: unlinked[0] }));
    return;
  }
  const emptyCatalogOutfit = bareOutfitWithoutCatalogPrompt(prompt);
  if (emptyCatalogOutfit) {
    const template = getCreateVideoT(
      'free_image_unlinked_helper',
      'Select {name} from the Character, Outfit, or Background helpers so we can link it.',
    );
    showError(interpolateCreditTemplate(template, { name: emptyCatalogOutfit }));
    return;
  }
  const negativePrompt = els.negative?.value?.trim() || '';
  const nsfwBlock = await getBlockedFilteredImagePromptMessage([prompt, negativePrompt].filter(Boolean).join(', '));
  if (nsfwBlock) {
    showError(nsfwBlock, { duration: 0, closable: true });
    return;
  }
  const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage([prompt, negativePrompt].filter(Boolean).join(', '), {
    isLinkedBraceToken: isFreeLinkedBraceToken,
  });
  if (unrecognizedBlock) {
    showError(unrecognizedBlock, { duration: 0, closable: true });
    return;
  }

  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }

  const seed = imageSeedControl?.readSeed() ?? null;
  if (seed == null) {
    showError(getCreateVideoT('seed_invalid', 'Enter a seed between 1 and 2147483647'));
    return;
  }

  const loras = collectLoras(els);
  if (loras.length > MAX_LORAS) {
    showError(getCreateVideoT('free_image_lora_limit', 'Remove a LoRA before creating an image (max 3).'));
    return;
  }
  const style = getSelectedStyle(els);
  const size = readClampedSize(els);
  if (!size.ok) {
    showError(getCreateVideoT(
      'free_image_size_max',
      'Maximum dimensions are 1024x1024, please reduce either width or height',
    ));
    return;
  }
  const cost = computeStandaloneImageCost({
    style,
    loraCount: loras.length,
    width: size.width,
    height: size.height,
  });
  if (cost > getUserCredits()) {
    showError(getCreateVideoT('free_image_insufficient_credits', 'Insufficient credits'));
    return;
  }

  els.submit.disabled = true;
  setButtonBusy(els.submit, true);
  setPreviewVisible(els, true);
  setHdActionState(els, { mode: 'hidden' });
  setPreviewCardState(els.previewCard, {
    loading: true,
    placeholderText: getCreateVideoT('generating_preview', 'Generating...'),
  });

  let pendingUrl = null;
  let outputKey = null;
  try {
    const result = await fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/schedule-standalone-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: {
        prompt,
        negative_prompt: negativePrompt,
        style,
        loras,
        promptRefs: promptRefsMatchingPrompt(promptRefs, prompt),
        remove_background: Boolean(els.removeBackground?.checked),
        width: size.width,
        height: size.height,
        seed,
      },
      skipCache: true,
    });

    await syncSpriteCredits();
    updateCredits(els);
    imageSeedControl?.rollAfterQueue();
    showInfo(getCreateVideoT('free_image_queued', 'Image queued for generation'));

    pendingUrl = result?.pendingUrl;
    outputKey = result?.output_key || result?.outputKey || parseOutputKeyFromPendingUrl(pendingUrl);
    if (!pendingUrl) {
      throw new Error(getCreateVideoT('free_image_queue_failed', 'Failed to queue image'));
    }
  } catch (error) {
    stopEtaOverlay(els);
    setPreviewCardState(els.previewCard, {
      placeholderText: error.body?.error || error.message || getCreateVideoT('free_image_failed', 'Failed to create image'),
    });
    showError(error.body?.error || error.message || getCreateVideoT('free_image_failed', 'Failed to create image'));
    return;
  } finally {
    setButtonBusy(els.submit, false);
    validateForm(els);
  }

  void waitForPreviewJob(els, {
    pendingUrl,
    outputKey,
    style,
    loraCount: loras.length,
    width: size.width,
    height: size.height,
  });
};

const handleUpgradeHd = async (els) => {
  const job = previewJob;
  if (!job?.outputKey || job.isHd) return;
  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }
  const { cost } = hdUpgradeCostLabel({
    style: job.style,
    loraCount: job.loraCount,
    width: job.width,
    height: job.height,
  });
  if (cost > getUserCredits()) {
    showError(getCreateVideoT('free_image_insufficient_credits', 'Insufficient credits'));
    return;
  }

  setButtonBusy(els.upgradeHd, true);
  els.upgradeHd.disabled = true;
  try {
    const result = await requestStandaloneHdUpgrade({ token, outputKey: job.outputKey });
    await syncSpriteCredits();
    updateCredits(els);
    const pendingUrl = result?.pendingUrl || job.pendingUrl;
    await waitForPreviewJob(els, {
      pendingUrl,
      outputKey: job.outputKey,
      style: job.style,
      loraCount: job.loraCount,
      width: job.width,
      height: job.height,
      keepImage: true,
      markHd: true,
    });
  } catch (error) {
    showError(error.body?.error || error.message || getCreateVideoT('free_image_failed', 'Failed to create image'));
    setHdActionState(els, {
      mode: 'upgrade',
      style: job.style,
      loraCount: job.loraCount,
      width: job.width,
      height: job.height,
    });
  } finally {
    setButtonBusy(els.upgradeHd, false);
    if (els.upgradeHd) els.upgradeHd.disabled = false;
  }
};

let freeHelpersReady = false;
const ensureFreeHelpers = ({ getCharacters } = {}) => {
  if (freeHelpersReady) return;
  freeHelpersReady = true;
  initializeNegativePromptAutocomplete('free-image-negative')
    .catch((error) => console.warn('init standalone negative autocomplete failed', error));
  initStandalonePromptHelpers({
    textareaId: 'free-image-prompt',
    helperRootId: 'prompt-helper-free',
    pillsRootId: 'prompt-helper-pills-free',
    getCharacters,
    promptRefs,
    onCharacterSelected: (character, name) => {
      const liveEls = queryFreeImageElements();
      if (character.hasLora) ensureCharacterLoraRow(liveEls, name);
      updateCredits(liveEls);
    },
    onBareDefaultOutfit: () => {
      showInfo(getCreateVideoT(
        'free_image_default_outfit_needs_character',
        'The default outfit only works as {character:default}.',
      ));
    },
    onLocationError: () => {
      showError(getCreateVideoT('free_image_failed', 'Failed to create image'));
    },
    autocomplete: {
      suggestionType: 'spriteActionPrompt',
      options: {
        isLinkedBraceToken: isFreeLinkedBraceToken,
        forbiddenCategories: [5],
        replaceForbiddenCategories: true,
      },
    },
  });
};

export const initFreeImageFlow = ({
  initialKind = IMAGE_KINDS.SPRITE,
  getCharacters,
} = {}) => {
  resolveCharacters = typeof getCharacters === 'function' ? getCharacters : () => [];
  const els = queryFreeImageElements();
  if (!els.freePanel || !els.spritePanel) return;

  setImageKind(els, initialKind);
  setStyle(els, getStylePreference());
  if ((els.loraRows?.querySelectorAll('[data-free-lora-row]')?.length || 0) === 0) {
    addLoraRow(els);
  }
  updateCredits(els);

  els.kindTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const kind = tab.id === 'image-kind-free' ? IMAGE_KINDS.FREE : IMAGE_KINDS.SPRITE;
      setImageKind(els, kind);
      if (kind === IMAGE_KINDS.FREE) {
        ensureFreeHelpers({ getCharacters });
        updateCredits(els);
      }
    });
    tab.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      tab.click();
    });
  });

  els.styleAnime?.addEventListener('click', () => setStyle(els, 'anime'));
  els.styleRealism?.addEventListener('click', () => setStyle(els, 'realism'));
  applySizeToInputs(els, els.width?.value, els.height?.value);
  setAspectLock(els, false);
  if (els.ratioLock) {
    els.ratioLock.setAttribute('aria-label', getCreateVideoT('free_image_ratio_lock', 'Lock aspect ratio'));
  }
  els.sizePresets.forEach((btn) => {
    const handleSelectPreset = () => {
      const preset = STANDALONE_IMAGE_SIZE_PRESETS.find((entry) => entry.id === btn.dataset.sizePreset);
      if (!preset) return;
      applySizeToInputs(els, preset.width, preset.height, { refreshLockRatio: true });
    };
    btn.addEventListener('click', handleSelectPreset);
    btn.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleSelectPreset();
    });
  });
  const handleSizeBlur = (changed) => {
    if (aspectLockOn) {
      const raw = changed === 'width' ? els.width?.value : els.height?.value;
      const size = applyLockedStandaloneImageSize({ changed, value: raw, ratio: lockedRatio });
      writeSizeInputs(els, size);
      syncSizeChrome(els, size);
      updateCredits(els);
      return;
    }
    readClampedSize(els);
  };
  const handleSizeInput = (changed) => {
    if (applyingSize) return;
    if (aspectLockOn) {
      applyLockedChange(els, changed, changed === 'width' ? els.width?.value : els.height?.value);
      return;
    }
    const size = sizeFromInputs(els);
    syncSizeChrome(els, size);
    updateCredits(els);
  };
  els.width?.addEventListener('blur', () => handleSizeBlur('width'));
  els.height?.addEventListener('blur', () => handleSizeBlur('height'));
  els.width?.addEventListener('input', () => handleSizeInput('width'));
  els.height?.addEventListener('input', () => handleSizeInput('height'));
  els.width?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleSizeBlur('width');
  });
  els.height?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleSizeBlur('height');
  });
  const handleToggleLock = () => setAspectLock(els, !aspectLockOn);
  els.ratioLock?.addEventListener('click', handleToggleLock);
  els.ratioLock?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleToggleLock();
  });
  els.prompt?.addEventListener('input', () => syncCharacterLoraRows(els));
  els.addLora?.addEventListener('click', () => {
    addLoraRow(els);
    updateCredits(els);
  });
  imageSeedControl = bindGenerationSeedControl({
    input: els.seed,
    lockButton: els.seedLock,
    rerollButton: els.seedReroll,
    locked: false,
    lockedLabel: getCreateVideoT('seed_lock', 'Keep seed after generation'),
    unlockedLabel: getCreateVideoT('seed_randomize_after', 'Unlock to pick a new seed after each generation.'),
  });
  els.submit?.addEventListener('click', () => handleSubmitClick(els));
  els.upgradeHd?.addEventListener('click', () => handleUpgradeHd(els));
  bindLoraLimitTooltip(els);

  document.addEventListener('spriteCreditsUpdated', () => {
    updateCredits(els);
    if (previewJob && !previewJob.isHd) {
      setHdActionState(els, {
        mode: els.upgradeHd && !els.upgradeHd.classList.contains('hidden') ? 'upgrade' : 'hidden',
        style: previewJob.style,
        loraCount: previewJob.loraCount,
        width: previewJob.width,
        height: previewJob.height,
      });
    }
  });

  if (initialKind === IMAGE_KINDS.FREE) ensureFreeHelpers({ getCharacters });

  const similar = consumeSimilarStandaloneImage();
  if (similar) {
    setImageKind(els, IMAGE_KINDS.FREE);
    setStyle(els, similar.style);
    if (els.prompt) els.prompt.value = similar.prompt;
    if (els.negative) els.negative.value = similar.negative || '';
    if (similar.negative) openFreeImageAdvanced();
    els.loraRows?.replaceChildren();
    const loras = similar.loras.slice(0, MAX_LORAS);
    loras.forEach((lora) => {
      addLoraRow(els);
      const row = els.loraRows?.lastElementChild;
      const idInput = row?.querySelector('[data-free-lora-id]');
      const strengthInput = row?.querySelector('[data-free-lora-strength]');
      if (idInput) idInput.value = lora.id || '';
      if (strengthInput) strengthInput.value = lora.strength == null ? '1' : String(lora.strength);
    });
    if (loras.length === 0) addLoraRow(els);
    if (loras.length > 0) openFreeImageAdvanced();
    ensureFreeHelpers({ getCharacters });
    syncCharacterLoraRows(els);
    if (similar.job_id) void applySimilarPromptRefs(els, similar.job_id);
  }

  const urlParams = new URLSearchParams(window.location.search);
  const linkedSeed = similar?.seed ?? (urlParams.get('mode') === 'video-text' ? null : urlParams.get('seed'));
  if (parseGenerationSeed(linkedSeed) != null && imageSeedControl?.applySeed(linkedSeed)) {
    openFreeImageAdvanced();
  }
  const similarWidth = similar?.width ?? urlParams.get('width');
  const similarHeight = similar?.height ?? urlParams.get('height');
  if (similarWidth != null && similarWidth !== '' && similarHeight != null && similarHeight !== '') {
    applySizeToInputs(els, similarWidth, similarHeight, { refreshLockRatio: true });
  }
};
